/**
 * VacancyCostSlider.js — Landing-page interactive vacancy revenue loss calculator.
 *
 * Doctrine: "Vacancy cost analysis · $135/day → weekly → cumulative portfolio
 * revenue loss." Emphasizes financial urgency for asset owners visiting
 * moovinforward.org. Pure-frontend · zero backend calls.
 */
import React, { useState, useMemo } from "react";

const NAVY = "#020A1F"; const CYAN = "#00CFCF"; const CHROME = "#A0A0A0"; const INK = "#F5F5F7";

const wrap = {
  background: "rgba(3,11,26,0.85)",
  border: `1px solid ${CYAN}44`,
  borderRadius: 12,
  padding: "clamp(18px,3vw,28px)",
  marginTop: 24, marginBottom: 16,
  maxWidth: 640, boxSizing: "border-box",
};

const money = (n) => `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export default function VacancyCostSlider() {
  const [units, setUnits] = useState(12);
  const [dailyLoss, setDailyLoss] = useState(135);

  const numbers = useMemo(() => {
    const dailyPortfolio = units * dailyLoss;
    const weeklyPortfolio = dailyPortfolio * 7;
    const monthlyPortfolio = dailyPortfolio * 30;
    const yearlyPortfolio = dailyPortfolio * 365;
    return { dailyPortfolio, weeklyPortfolio, monthlyPortfolio, yearlyPortfolio };
  }, [units, dailyLoss]);

  return (
    <div style={wrap} data-testid="vacancy-cost-slider">
      <div style={{ fontSize: 11, letterSpacing: "0.2em", color: CYAN, marginBottom: 6 }}>
        VACANCY COST ANALYSIS
      </div>
      <h3 style={{ margin: 0, fontSize: "clamp(18px, 2.4vw, 22px)", color: INK }}>
        Every offline unit costs you every single day.
      </h3>
      <div style={{ color: CHROME, marginTop: 6, fontSize: 13 }}>
        Drag the sliders to see what your legacy portfolio is bleeding while units sit vacant.
      </div>

      <div style={{ marginTop: 18 }}>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: CHROME }}>
          <span>Offline units</span><strong style={{ color: INK }} data-testid="vcs-units">{units}</strong>
        </div>
        <input type="range" min="1" max="50" value={units} onChange={e => setUnits(+e.target.value)}
          style={{ width: "100%", accentColor: CYAN }} data-testid="vcs-units-slider" />
      </div>

      <div style={{ marginTop: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: CHROME }}>
          <span>Daily revenue loss per unit</span><strong style={{ color: INK }} data-testid="vcs-daily">{money(dailyLoss)}</strong>
        </div>
        <input type="range" min="60" max="350" step="5" value={dailyLoss} onChange={e => setDailyLoss(+e.target.value)}
          style={{ width: "100%", accentColor: CYAN }} data-testid="vcs-daily-slider" />
      </div>

      <div style={{ marginTop: 18, display: "grid", gap: 8, gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))" }}>
        {[
          { label: "Portfolio · daily", value: numbers.dailyPortfolio, testid: "vcs-daily-total" },
          { label: "Portfolio · weekly", value: numbers.weeklyPortfolio, testid: "vcs-weekly-total" },
          { label: "Portfolio · monthly", value: numbers.monthlyPortfolio, testid: "vcs-monthly-total" },
          { label: "Portfolio · yearly", value: numbers.yearlyPortfolio, testid: "vcs-yearly-total" },
        ].map(m => (
          <div key={m.label} style={{ background: "rgba(0,0,0,0.35)", padding: 12, borderRadius: 8, border: `1px solid ${CHROME}22` }}>
            <div style={{ fontSize: 10, color: CHROME, letterSpacing: "0.1em" }}>{m.label.toUpperCase()}</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: CYAN, marginTop: 4 }} data-testid={m.testid}>{money(m.value)}</div>
          </div>
        ))}
      </div>

      <div style={{ marginTop: 14, fontSize: 12, color: CHROME, fontStyle: "italic" }}>
        Stabilize the portfolio and the meter stops. That is the mandate.
      </div>
    </div>
  );
}
